import React, {FC, SyntheticEvent, useEffect, useState} from 'react';

import checkSuccess from 'assets/images/svg/check-success.svg';
import crossDanger from 'assets/images/svg/cross-danger.svg';
import pending from 'assets/images/svg/pending.svg';

import {Money as MoneyType, WithdrawRequest} from '../../../../api-client';
import {createReferralManager} from '../../../../di';
import {formatDate} from '../../../../formatters';
import {useErrorCallback} from '../../../../hooks';
import {cn} from '../../../../utils';
import {Button, Image, InputMoney, Money, PrimaryButton} from '../../../common';
import {PrimaryButtonColor} from '../../../common/buttons/decorators/PrimaryButton/PrimaryButton';
import {MoneyMode} from '../../../common/utils/Money/Money';
import {Block} from '../../../layout';
import styles from './ReferralPayout.module.scss';

interface Props {
  totalAccrued: MoneyType;
  totalAvailable: MoneyType;
  withdrawals: WithdrawRequest[];
}

const MIN_AMOUNT = 1000000;
const MIN_AMOUNT_MONEY = new MoneyType(MIN_AMOUNT, 'RUB');

const ReferralPayout: FC<Props> = ({totalAccrued, totalAvailable, withdrawals}) => {
  const [money, setMoney] = useState<MoneyType>(new MoneyType(MIN_AMOUNT, 'RUB'));
  const [noMoneyToWithdraw, setNoMoneyToWithdraw] = useState(false);
  const [loading, setLoading] = useState(false);
  const errorCallback = useErrorCallback();

  const getWithdrawnAmount = (withdrawals: WithdrawRequest[]): MoneyType => {
    const total = withdrawals
      .filter((w) => w.status === 'WITHDRAWN')
      .reduce((sum, w) => sum + w.money.amount, 0);
    return new MoneyType(total, 'RUB');
  };

  const getRequestedAmount = (withdrawals: WithdrawRequest[]): MoneyType => {
    const total = withdrawals.reduce((sum, w) => sum + w.money.amount, 0);
    return new MoneyType(total, 'RUB');
  };

  const getAvailableAmount = (totalAccrued: MoneyType, requested: MoneyType): MoneyType => {
    return new MoneyType(totalAccrued.amount - requested.amount, 'RUB');
  };

  const withdrawnTotal = getWithdrawnAmount(withdrawals);
  const requestedTotal = getRequestedAmount(withdrawals);
  const availableTotal = getAvailableAmount(totalAccrued, requestedTotal);

  const isSubmitDisabled =
    noMoneyToWithdraw || money.amount > availableTotal.amount || money.amount < MIN_AMOUNT;

  const onSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      const manager = await createReferralManager();
      await manager.create(money);
      window.location.reload();
    } catch (err: any) {
      await errorCallback(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setNoMoneyToWithdraw(totalAvailable.amount < MIN_AMOUNT);
  }, [availableTotal]);

  const getStatusIcon = (status: string): React.ReactNode => {
    switch (status) {
    case 'WITHDRAWN':
      return <Image className={styles.paymentsGrid__icon} src={checkSuccess} alt="withdrawn" />;
    case 'NEW':
      return <Image className={styles.paymentsGrid__icon} src={pending} alt="pending" />;
    case 'CANCELED':
    case 'CANCELED_BY_USER':
    case 'CANCELED_BY_ADMIN':
    default:
      return <Image className={styles.paymentsGrid__icon} src={crossDanger} alt="canceled" />;
    }
  };

  return (
    <div className={styles.referralPayout}>
      <Block>
        <Block.Content>
          <Block.Body withPadding>
            <div className={styles.payoutGrid}>
              <div className={styles.available}>
                <div className={styles.availableAmount}>
                  <Money money={totalAvailable} />
                </div>
                <div className={styles.label}>Доступно</div>
              </div>
              <div className={styles.secondaryStats}>
                <div className={styles.stat}>
                  <div className={styles.amount}>
                    <Money money={totalAccrued} />
                  </div>
                  <div className={styles.label}>Начислено</div>
                </div>
                <div className={styles.stat}>
                  <div className={styles.amount}>
                    <Money money={withdrawnTotal} />
                  </div>
                  <div className={styles.label}>Выплачено</div>
                </div>
              </div>
            </div>
          </Block.Body>
        </Block.Content>
      </Block>

      <Block>
        <Block.Content>
          <Block.Header withPadding>
            <div className={styles.cardHeading}>Вывести средства</div>
          </Block.Header>
          <Block.Body withPadding>
            <div className={styles.sendButtons}>
              <InputMoney money={money} onValueChanged={(money) => setMoney(money)}></InputMoney>
              <PrimaryButton color={PrimaryButtonColor.GREEN} expanded={true}>
                <Button disabled={isSubmitDisabled} onClick={onSubmit}>
                  {loading ? 'Запрос...' : 'Запросить'}
                </Button>
              </PrimaryButton>
              <div className={styles.sendButtons__minMark}>
                Минимальный размер запроса на вывод{' '}
                <Money mode={MoneyMode.WRITE_OFF} money={MIN_AMOUNT_MONEY} />
              </div>
            </div>
          </Block.Body>
        </Block.Content>
      </Block>

      <Block>
        <Block.Content>
          <Block.Header withPadding>
            <div className={styles.cardHeading}>Выплаты</div>
          </Block.Header>
          <Block.Body withPadding>
            <div className={styles.paymentsGrid__table}>
              <div className={styles.paymentsGrid__header}>
                <div className={styles.paymentsGrid__cell}></div>
                <div className={styles.paymentsGrid__cell}>Дата</div>
                <div className={styles.paymentsGrid__cell}>Брутто</div>
                <div className={styles.paymentsGrid__cell}>НДФЛ</div>
                <div className={styles.paymentsGrid__cell}>Нетто</div>
              </div>

              {withdrawals && withdrawals.length > 0
                ? withdrawals.map((withdraw) => {
                  const ndfl = new MoneyType(withdraw.ndfl.amount, withdraw.ndfl.currencyCode);
                  const netto = new MoneyType(
                    withdraw.money.amount - withdraw.ndfl.amount,
                    withdraw.money.currencyCode
                  );

                  return (
                    <div className={styles.paymentsGrid__row} key={withdraw.uuid}>
                      <div className={styles.paymentsGrid__cell}>
                        {getStatusIcon(withdraw.status)}
                      </div>
                      <div className={styles.paymentsGrid__cell}>{formatDate(withdraw.date)}</div>
                      <div className={cn(styles.paymentsGrid__cell, styles.moneyWrapper)}>
                        {withdraw.money ? <Money money={withdraw.money} /> : '—'}
                      </div>
                      <div className={cn(styles.paymentsGrid__cell, styles.moneyWrapper)}>
                        {withdraw.money ? <Money money={ndfl} /> : '—'}
                      </div>
                      <div className={cn(styles.paymentsGrid__cell, styles.moneyWrapper)}>
                        {netto ? <Money money={netto} /> : '—'}
                      </div>
                    </div>
                  );
                })
                : [1, 2, 3].map((n) => {
                  return (
                    <div className={styles.paymentsGrid__row} key={n}>
                      <div className={styles.paymentsGrid__cell}>{''}</div>
                      <div className={cn(styles.paymentsGrid__cell, styles.blank)}>{'—'}</div>
                      <div className={cn(styles.paymentsGrid__cell, styles.blank)}>{'—'}</div>
                      <div className={cn(styles.paymentsGrid__cell, styles.blank)}>{'—'}</div>
                      <div className={cn(styles.paymentsGrid__cell, styles.blank)}>{'—'}</div>
                    </div>
                  );
                })}
            </div>
          </Block.Body>
        </Block.Content>
      </Block>
    </div>
  );
};

export default ReferralPayout;
